export type HessCampaignAction = "turned_off" | "promoted" | "other";

export interface HessSkuYoYMonth {
  monthStart: string;
  currentSales: number;
  yagoSales: number;
  salesDelta: number;
  salesDeltaPct: number | null;
  currentUnits: number;
  yagoUnits: number;
  unitDelta: number;
  unitDeltaPct: number | null;
}

export interface HessSkuYoYTotals extends Omit<HessSkuYoYMonth, "monthStart"> {}

export interface HessTargetSkuMapping {
  id: string;
  sku: string;
  name: string;
  shortName: string;
  campaignAction: Exclude<HessCampaignAction, "other">;
  matchMethod: "product_alias" | "inferred_product_alias";
  aliases: Array<{
    entityBrandName: string;
    productName: string;
  }>;
  notes: string;
}

export interface HessTargetSkuYoYProduct extends HessSkuYoYTotals {
  id: string;
  sku: string;
  name: string;
  shortName: string;
  campaignAction: Exclude<HessCampaignAction, "other">;
  matchMethod: "product_alias" | "inferred_product_alias";
  notes: string;
  sourceNames: string[];
  sourceCodes: string[];
  months: HessSkuYoYMonth[];
}

export interface HessAllSkuYoYProduct extends HessSkuYoYTotals {
  id: string;
  brand: string;
  name: string;
  category: string;
  campaignAction: HessCampaignAction;
  sourceCodes: string[];
}

export interface HessSkuYoYDashboardData {
  sourceFile: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  recordsProcessed: number;
  skuCodeQualityNote: string;
  summary: HessSkuYoYTotals;
  monthlyTotals: HessSkuYoYMonth[];
  targetProducts: HessTargetSkuYoYProduct[];
  allProducts: HessAllSkuYoYProduct[];
}

interface HessRawSkuRow {
  reportDate: string;
  entityBrandName: string;
  classifiedCode: string;
  category: string;
  dollarVolume: number;
  units: number;
  dollarVolumeYago: number;
  unitsYago: number;
  productName: string;
}

const REQUIRED_COLUMNS = [
  "report_date",
  "entity_brand_name",
  "classified_code",
  "category",
  "dollar_volume",
  "units",
  "dollar_volume_yago",
  "units_yago",
  "product_name",
] as const;

export const HESS_ALL_SKU_CSV_PATH = "/data/hess_all_sku.csv";

export const HESS_TARGET_SKU_MAPPINGS: HessTargetSkuMapping[] = [
  {
    id: "hs-sauvignon-blanc",
    sku: "00717888570029",
    name: "Hess Select Sauvignon Blanc",
    shortName: "Sauvignon Blanc",
    campaignAction: "turned_off",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS SELECT",
        productName: "Sauvignon Blanc, California",
      },
    ],
    notes:
      "Source UPC appears as scientific notation; matched to the Hess Select Sauvignon Blanc product row.",
  },
  {
    id: "hs-pinot-gris",
    sku: "00717888557921",
    name: "Hess Select Pinot Gris",
    shortName: "Pinot Gris",
    campaignAction: "turned_off",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS SELECT",
        productName: "Pinot Gris, California",
      },
    ],
    notes:
      "Source UPC appears as scientific notation; matched to the Hess Select Pinot Gris product row.",
  },
  {
    id: "hs-chardonnay",
    sku: "00717888500224",
    name: "Hess Select Chardonnay",
    shortName: "Chardonnay",
    campaignAction: "turned_off",
    matchMethod: "inferred_product_alias",
    aliases: [
      {
        entityBrandName: "HESS SELECT",
        productName: "Chardonnay",
      },
    ],
    notes:
      "Exact UPC is not preserved in the CSV; inferred from the Hess Select Chardonnay row.",
  },
  {
    id: "hs-pinot-noir",
    sku: "00717888506028",
    name: "Hess Select Pinot Noir",
    shortName: "Pinot Noir",
    campaignAction: "turned_off",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS SELECT",
        productName: "Pinot Noir, California",
      },
    ],
    notes:
      "Source UPC appears as scientific notation; matched to the Hess Select Pinot Noir product row.",
  },
  {
    id: "allomi-cabernet",
    sku: "00717888410226",
    name: "Allomi Cabernet Sauvignon",
    shortName: "Allomi Cab",
    campaignAction: "promoted",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS ESTATE",
        productName: "Cabernet Sauvignon, Allomi, Napa Valley",
      },
      {
        entityBrandName: "HESS",
        productName: "Cabernet Sauvignon, Allomi, Napa Valley",
      },
    ],
    notes:
      "Matched to Allomi Cabernet Sauvignon rows across Hess Estate and Hess entity names.",
  },
  {
    id: "mvrk-cabernet",
    sku: "00717888000113",
    name: "MVRK Cabernet Sauvignon",
    shortName: "MVRK Cab",
    campaignAction: "promoted",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS",
        productName: "MVRK Cabernet Sauvignon, Paso Robles",
      },
    ],
    notes: "Matched to MVRK Cabernet Sauvignon, Paso Robles.",
  },
  {
    id: "hs-cabernet",
    sku: "00717888510025",
    name: "Hess Select Cabernet Sauvignon",
    shortName: "HS Cab",
    campaignAction: "promoted",
    matchMethod: "product_alias",
    aliases: [
      {
        entityBrandName: "HESS SELECT",
        productName: "Cabernet Sauvignon",
      },
    ],
    notes:
      "Source UPC appears as scientific notation; matched to the Hess Select Cabernet Sauvignon row.",
  },
];

const emptyTotals = (): HessSkuYoYTotals => ({
  currentSales: 0,
  yagoSales: 0,
  salesDelta: 0,
  salesDeltaPct: null,
  currentUnits: 0,
  yagoUnits: 0,
  unitDelta: 0,
  unitDeltaPct: null,
});

const normalizeHeader = (value: string) => value.trim();

const toNumber = (value: string | undefined) => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

const percentChange = (current: number, previous: number) => {
  if (previous === 0) return null;
  return (current - previous) / previous;
};

const finalizeTotals = (totals: HessSkuYoYTotals): HessSkuYoYTotals => {
  const currentSales = roundCurrency(totals.currentSales);
  const yagoSales = roundCurrency(totals.yagoSales);
  const currentUnits = Math.round(totals.currentUnits);
  const yagoUnits = Math.round(totals.yagoUnits);

  return {
    currentSales,
    yagoSales,
    salesDelta: roundCurrency(currentSales - yagoSales),
    salesDeltaPct: percentChange(currentSales, yagoSales),
    currentUnits,
    yagoUnits,
    unitDelta: currentUnits - yagoUnits,
    unitDeltaPct: percentChange(currentUnits, yagoUnits),
  };
};

const toMonthStart = (value: string) => {
  const parts = value.trim().split(/[/-]/).map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return value;
  }

  const [month, , year] = parts;
  const fullYear = year < 100 ? 2000 + year : year;
  return `${fullYear}-${String(month).padStart(2, "0")}-01`;
};

const makeProductId = (brand: string, name: string) =>
  `${brand}-${name}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(field);
      field = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") {
        i += 1;
      }
      row.push(field);
      if (row.some((cell) => cell.length > 0)) {
        rows.push(row);
      }
      row = [];
      field = "";
      continue;
    }

    field += char;
  }

  row.push(field);
  if (row.some((cell) => cell.length > 0)) {
    rows.push(row);
  }

  return rows;
};

const parseHessRows = (csvText: string): HessRawSkuRow[] => {
  const rows = parseCsvRows(csvText);
  const [headerRow, ...dataRows] = rows;

  if (!headerRow) {
    throw new Error("Hess SKU CSV is empty.");
  }

  const headers = headerRow.map(normalizeHeader);
  const missingColumns = REQUIRED_COLUMNS.filter(
    (column) => !headers.includes(column),
  );

  if (missingColumns.length > 0) {
    throw new Error(
      `Hess SKU CSV is missing required columns: ${missingColumns.join(", ")}`,
    );
  }

  const indexFor = (column: string) => headers.indexOf(column);

  return dataRows.map((row) => ({
    reportDate: row[indexFor("report_date")]?.trim() ?? "",
    entityBrandName: row[indexFor("entity_brand_name")]?.trim() ?? "",
    classifiedCode: row[indexFor("classified_code")]?.trim() ?? "",
    category: row[indexFor("category")]?.trim() ?? "",
    dollarVolume: toNumber(row[indexFor("dollar_volume")]),
    units: toNumber(row[indexFor("units")]),
    dollarVolumeYago: toNumber(row[indexFor("dollar_volume_yago")]),
    unitsYago: toNumber(row[indexFor("units_yago")]),
    productName: row[indexFor("product_name")]?.trim() ?? "",
  }));
};

const sourceNameFor = (row: HessRawSkuRow) =>
  `${row.entityBrandName} | ${row.productName}`;

const rowMatchesAliases = (
  row: HessRawSkuRow,
  mapping: HessTargetSkuMapping,
) =>
  mapping.aliases.some(
    (alias) =>
      row.entityBrandName === alias.entityBrandName &&
      row.productName === alias.productName,
  );

const aggregateRows = (rows: HessRawSkuRow[]) => {
  const totals = emptyTotals();
  const monthly = new Map<string, HessSkuYoYTotals>();

  rows.forEach((row) => {
    const monthStart = toMonthStart(row.reportDate);
    const monthTotals = monthly.get(monthStart) ?? emptyTotals();

    totals.currentSales += row.dollarVolume;
    totals.yagoSales += row.dollarVolumeYago;
    totals.currentUnits += row.units;
    totals.yagoUnits += row.unitsYago;

    monthTotals.currentSales += row.dollarVolume;
    monthTotals.yagoSales += row.dollarVolumeYago;
    monthTotals.currentUnits += row.units;
    monthTotals.yagoUnits += row.unitsYago;

    monthly.set(monthStart, monthTotals);
  });

  const monthlyTotals: HessSkuYoYMonth[] = Array.from(monthly.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthStart, totalsForMonth]) => ({
      monthStart,
      ...finalizeTotals(totalsForMonth),
    }));

  return {
    totals: finalizeTotals(totals),
    monthlyTotals,
  };
};

export const parseHessSkuYoYCsv = (
  csvText: string,
  sourceFile = "hess_all_sku.csv",
): HessSkuYoYDashboardData => {
  const rows = parseHessRows(csvText);
  const portfolio = aggregateRows(rows);

  const targetProducts = HESS_TARGET_SKU_MAPPINGS.map((mapping) => {
    const matchingRows = rows.filter((row) => rowMatchesAliases(row, mapping));
    const aggregate = aggregateRows(matchingRows);

    return {
      id: mapping.id,
      sku: mapping.sku,
      name: mapping.name,
      shortName: mapping.shortName,
      campaignAction: mapping.campaignAction,
      matchMethod: mapping.matchMethod,
      notes: mapping.notes,
      sourceNames: Array.from(new Set(matchingRows.map(sourceNameFor))).sort(),
      sourceCodes: Array.from(
        new Set(matchingRows.map((row) => row.classifiedCode)),
      ).sort(),
      ...aggregate.totals,
      months: aggregate.monthlyTotals,
    };
  });

  const actionBySourceName = new Map<string, HessCampaignAction>();
  targetProducts.forEach((product) => {
    product.sourceNames.forEach((sourceName) => {
      actionBySourceName.set(sourceName, product.campaignAction);
    });
  });

  const productRows = new Map<string, HessRawSkuRow[]>();
  rows.forEach((row) => {
    const key = sourceNameFor(row);
    productRows.set(key, [...(productRows.get(key) ?? []), row]);
  });

  const allProducts = Array.from(productRows.entries())
    .map(([sourceName, productRowsForKey]) => {
      const [brand, name] = sourceName.split(" | ");
      const aggregate = aggregateRows(productRowsForKey);

      return {
        id: makeProductId(brand, name),
        brand,
        name,
        category: productRowsForKey[0]?.category ?? "",
        campaignAction: actionBySourceName.get(sourceName) ?? "other",
        sourceCodes: Array.from(
          new Set(productRowsForKey.map((row) => row.classifiedCode)),
        ).sort(),
        ...aggregate.totals,
      };
    })
    .sort((a, b) => b.currentSales - a.currentSales);

  const monthlyTotals = portfolio.monthlyTotals;
  const dateRangeStart = monthlyTotals[0]?.monthStart ?? "";
  const dateRangeEnd = monthlyTotals[monthlyTotals.length - 1]?.monthStart ?? "";

  return {
    sourceFile,
    dateRangeStart,
    dateRangeEnd,
    recordsProcessed: rows.length,
    skuCodeQualityNote:
      "Many classified_code values are scientific notation in the source CSV; impacted SKU matching uses product-name aliases where exact UPCs are unavailable.",
    summary: portfolio.totals,
    monthlyTotals,
    targetProducts,
    allProducts,
  };
};
